import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, Loader2, KeyRound, Users, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';

export default function FinanceStaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/finance-staff');
      setStaff(r.data?.staff || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load finance staff');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password) { toast.error('Email & password required'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 chars'); return; }
    setCreating(true);
    try {
      await api.post('/api/admin/finance-staff', form);
      toast.success('Finance staff created');
      setForm({ email: '', password: '', name: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create staff');
    } finally { setCreating(false); }
  };

  const toggleEnabled = async (id, enabled) => {
    try {
      await api.put(`/api/admin/finance-staff/${id}`, { enabled });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const resetPassword = async (id) => {
    const pwd = prompt('Enter new password for this finance user (min 8 chars):');
    if (!pwd) return;
    if (pwd.length < 8) { toast.error('Password must be at least 8 chars'); return; }
    try {
      await api.put(`/api/admin/finance-staff/${id}`, { password: pwd });
      toast.success('Password updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const remove = async (id, email) => {
    if (!confirm(`Delete finance staff ${email}? They will immediately lose access.`)) return;
    try {
      await api.delete(`/api/admin/finance-staff/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="finance-staff-page">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Shield className="w-7 h-7 text-blue-400" />
          Finance Staff
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Create login accounts for finance team members. They sign in at{' '}
          <a href="/finance/auth" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline inline-flex items-center gap-1" data-testid="finance-portal-link">
            /finance/auth <ExternalLink className="w-3 h-3" />
          </a>{' '}
          and can manage manual payment methods across <b>all tenants</b>. They cannot access any other area.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131822] p-5">
        <div className="text-white font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Finance Staff
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input
            placeholder="email@example.com"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="bg-[#0B0E14] border-white/10 text-white"
            data-testid="finance-staff-email"
          />
          <Input
            placeholder="Display name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="bg-[#0B0E14] border-white/10 text-white"
            data-testid="finance-staff-name"
          />
          <Input
            type="password"
            placeholder="Password (min 8)"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="bg-[#0B0E14] border-white/10 text-white"
            data-testid="finance-staff-password"
          />
          <Button onClick={create} disabled={creating} data-testid="finance-staff-create">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Staff
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131822]">
        <div className="px-5 py-4 border-b border-white/5 text-white font-semibold flex items-center gap-2">
          <Users className="w-4 h-4" /> {staff.length} Finance Staff
        </div>
        {loading ? (
          <div className="p-6 text-gray-400 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : staff.length === 0 ? (
          <div className="p-6 text-gray-500 text-sm">No finance staff yet. Add one above.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {staff.map((s) => (
              <div key={s.id} className="p-5 flex items-center justify-between gap-3" data-testid={`finance-staff-row-${s.id}`}>
                <div>
                  <div className="text-white font-medium">{s.name || s.email}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Created {s.created_at?.slice(0, 10) || '—'}
                    {s.last_login ? ` · last login ${s.last_login.slice(0, 10)}` : ' · never logged in'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-400">
                    <Switch checked={!!s.enabled} onCheckedChange={(v) => toggleEnabled(s.id, v)} />
                    {s.enabled ? 'Enabled' : 'Disabled'}
                  </label>
                  <Button variant="outline" onClick={() => resetPassword(s.id)} data-testid={`finance-staff-reset-${s.id}`}>
                    <KeyRound className="w-4 h-4 mr-2" /> Reset Password
                  </Button>
                  <Button variant="outline" onClick={() => remove(s.id, s.email)} data-testid={`finance-staff-delete-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
